import {
  Link, Navbar, NavbarBrand, NavbarContent, NavbarItem, NavbarMenu, NavbarMenuItem, NavbarMenuToggle,
} from "@heroui/react";
import type { CSSProperties, FC } from "react";
import { useMemo, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Logo } from "@/entities/logo";
import type { IComponentBaseProps } from "@/shared/config";
import { useCN } from "@/shared/lib/hooks";
import { Btn } from "@/shared/ui/btn";
import type { IHeader, THeaderCtx } from "../config";
import { ctx as HeaderContext } from "../config";

const BurgerIcon = ({ isOpen }: { isOpen: boolean; }) => (
  <div className={"w-11 h-11 relative flex items-center justify-center"}>
    <span className={`absolute w-5 h-0.5 bg-white rounded transition-transform duration-200 ${
      isOpen ? "rotate-45" : "-translate-y-1.5"
    }`} />
    <span className={`absolute w-5 h-0.5 bg-white rounded transition-opacity duration-200 ${
      isOpen ? "opacity-0" : "opacity-100"
    }`} />
    <span className={`absolute w-5 h-0.5 bg-white rounded transition-transform duration-200 ${
      isOpen ? "-rotate-45" : "translate-y-1.5"
    }`} />
  </div>
);

/**
 * Виджет шапки сайта
 * @constructor
 */
export const Header: FC<IHeader & IComponentBaseProps> = ({
  baseClass = "header",
  isDefaultBlurred = false,
}) => {

  const { getCN } = useCN(baseClass);

  const [ isMenuOpen, setIsMenuOpen ] = useState(false);
  const [ isBlurred, setIsBlurred ] = useState(isDefaultBlurred);

  const location = useLocation();

  const isHomePage = useMemo(() => location.pathname === "/", []);

  const links = [
    { id: "advantages", title: "AI tools" },
    { id: "pricing", title: "Pricing" },
    { id: "faq", title: "FAQ" },
  ];

  const onLinkClick = (id: string) => {
    const selector = `#${id}`;

    if (isHomePage) {
      const targetElement = document.getElementById(id);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

        window.history.pushState(null, "", selector);
        setIsMenuOpen(false);
      }
    }
  };

  useEffect(() => {
    if (isHomePage && location.hash) {
      const anchorId = location.hash.replace("#", "");
      const refId = requestAnimationFrame(() => {
        const targetElement = document.getElementById(anchorId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          window.history.pushState(null, "", `#${anchorId}`);
        }
      });

      return () => cancelAnimationFrame(refId);
    }
  }, []);

  useEffect(() => {
    const container = document.querySelector("[data-js-page-layout]");

    if (!container) {
      return;
    }

    const handleScroll = () => {
      setIsBlurred(container.scrollTop > 10 || isDefaultBlurred);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const ctx = useMemo<THeaderCtx>(() => {
    return { getCN };
  }, []);

  return (
    <HeaderContext.Provider value={ctx}>
      <Navbar
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        isBlurred={false}
        className={`transition-colors h-20 duration-300 ${isBlurred ? "bg-codGray" : "bg-transparent"} mobile:h-15`}
        classNames={{ wrapper: "container" }}
      >
        <div className={"header-inner select-none w-full flex items-center justify-between mobile:pt-0"}>

          <NavbarBrand className={"max-w-35 w-full tablet:max-w-55"}>
            <Logo />
          </NavbarBrand>

          <NavbarContent
            className={"max-w-80 w-full gap-12 hidden tablet:flex desktop:flex tablet:gap-10"}
            justify={"center"}
            style={{ "--hover-opacity": "1" } as CSSProperties}
          >
            {links.map(({ id, title }) => (
              <NavbarItem key={id}>
                <Link
                  href={`/#${id}`}
                  onPress={() => onLinkClick(id)}
                  className={"text-white hover:text-jaffa transition-colors hover:opacity-100"}
                >
                  {title}
                </Link>
              </NavbarItem>
            ))}
          </NavbarContent>

          <NavbarContent className={"max-w-35 hidden tablet:flex desktop:flex"} justify={"end"}>
            <NavbarItem>
              <Btn href={`/#pricing`} typeStyle={"ghost"} onPress={() => onLinkClick("pricing")}>
                <span className={"isGradientDarkText"}>Get Started</span>
              </Btn>
            </NavbarItem>
          </NavbarContent>

          <NavbarMenuToggle
            className={"text-white w-11 flex tablet:hidden desktop:hidden"}
            aria-label={"Toggle menu"}
            icon={<BurgerIcon isOpen={isMenuOpen} />}
          />
        </div>

        <NavbarMenu className={"bg-codGray px-4 justify-between"} style={{ "--hover-opacity": "1", "--navbar-height": "7rem" } as CSSProperties}>
          <div className={"flex flex-col space-y-6 py-6 border-t border-scorpion text-center text-4xl"}>
            {links.map(({ id, title }) => (
              <NavbarMenuItem key={id}>
                <Link
                  href={`/#${id}`}
                  onPress={() => onLinkClick(id)}
                  className={"text-white hover:text-jaffa transition-colors hover:opacity-100"}
                >
                  {title}
                </Link>
              </NavbarMenuItem>
            ))}
          </div>
          <Btn
            href={`/#pricing`}
            typeStyle={"ghost"}
            className={"mt-auto mb-8"}
            onPress={() => onLinkClick("pricing")}
          >
            <span className={"isGradientDarkText"}>Get Started</span>
          </Btn>
        </NavbarMenu>
      </Navbar>
    </HeaderContext.Provider>
  );
};
